import React, { useState, useEffect, useMemo, useRef } from 'react'
import styles from './AiReadinessOutcome.module.css'
import Logo from "../../assets/SchoolAiLogo.svg"
import { aiReadinessOutcomes } from '../../data/aiReadinessOutcomes'
import { useAiReadiness } from '../../context/AiReadinessContext.jsx'
import CalendarModal from '../shared/CalendarModal'
import FormModal from '../../components/shared/modals/FormModal'
import { useNavigate } from 'react-router-dom'

export default function AiReadinessOutcome() {
    const [isMobile, setIsMobile] = useState(false)
    const containerRef = useRef(null)
    const demoButtonRef = useRef(null)
    const buttonTopHeightRef = useRef(0)
    const [showFloatingButton, setShowFloatingButton] = useState(false)
    const [openDemo, setOpenDemo] = useState(false)
    const navigate = useNavigate()

    const { formData, outcomeId } = useAiReadiness()

    // Determine which outcome to display
    const outcome = useMemo(() => {
        if (outcomeId) {
            const index = aiReadinessOutcomes.findIndex(o => o.outcome_id === outcomeId)
            return aiReadinessOutcomes?.[index] || aiReadinessOutcomes?.[0]
        }
        return aiReadinessOutcomes?.[0] || {}
    }, [outcomeId])
    const displayCity = formData?.city || ""
    const displaySchool = formData?.schoolName || ""

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    // Calculate button top height - recalculate when isMobile or outcome changes
    useEffect(() => {
        const timer = setTimeout(() => {
            if (demoButtonRef.current && containerRef.current) {
                const buttonRect = demoButtonRef.current.getBoundingClientRect()
                const containerRect = containerRef.current.getBoundingClientRect()
                const buttonTopPos = buttonRect.top - containerRect.top + containerRef.current.scrollTop
                buttonTopHeightRef.current = buttonTopPos
            }
        }, 100)
        return () => clearTimeout(timer)
    }, [outcome, isMobile])

    useEffect(() => {
        const element = containerRef.current
        if (!element) return

        const handleScroll = () => {
            const offset = isMobile ? 20 : 50
            if (element.scrollTop - offset > buttonTopHeightRef.current) {
                setShowFloatingButton(true)
            } else {
                setShowFloatingButton(false)
            }
        }

        element.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            element.removeEventListener('scroll', handleScroll, { passive: true })
        }
    }, [isMobile])


    return (
        <div className={styles.mainContainer} ref={containerRef}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.schoolInfo}>
                    <div className={styles.nameWrapper}>
                        <h1 className={styles.schoolName}>{displaySchool}</h1>
                        <span className={styles.reportLabel}>AI-Readiness Report</span>
                    </div>
                    <p className={styles.city}>{displayCity}</p>
                </div>
                <div className={styles.logoWrapper}>
                    <img src={Logo} alt="coschool" className={styles.logo} onClick={() => { navigate("/") }} />
                </div>
            </div>

            {/* Main Content */}
            <div className={styles.mainContent}>
                {/* Card Container */}
                <div className={styles.card} style={{ '--gradient-bg': outcome?.gradient }}>
                    {/* Top Section - Title + Description + Button + Right Illustration */}
                    <div className={styles.topSection}>
                        {isMobile ? (
                            <>
                                <h2 className={styles.mainTitle}>{outcome?.title}</h2>
                                <div className={styles.rightIllustration}>
                                    <img
                                        src={outcome?.mainImage?.component}
                                        alt={outcome?.mainImage?.alt}
                                        loading="lazy"
                                    />
                                </div>
                                <div className={styles.leftContent}>
                                    <p className={styles.description}>
                                        {outcome?.description}
                                    </p>
                                    <button ref={demoButtonRef} className={styles.demoButton}
                                        onClick={() => { setOpenDemo(true) }}>
                                        {outcome?.buttonText}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.leftContent}>
                                    <h2 className={styles.mainTitle}>{outcome?.title}</h2>
                                    <p className={styles.description}>
                                        {outcome?.description}
                                    </p>
                                    <button ref={demoButtonRef} className={styles.demoButton}
                                        onClick={() => { setOpenDemo(true) }}
                                    >
                                        {outcome?.buttonText}
                                    </button>
                                </div>
                                <div className={styles.rightIllustration}>
                                    <img
                                        src={outcome?.mainImage?.component}
                                        alt={outcome?.mainImage?.alt}
                                        loading="lazy"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Section 2: Contains three sub-sections */}
                    <div className={styles.section2Wrapper}>
                        {outcome?.sections?.map((section, index) => {
                            const sectionClass = index === 0 ? styles.section2 : (index === 1 ? styles.section3 : styles.section4)
                            const isEvenSection = index === 1

                            return (
                                <div key={section?.id} className={sectionClass}>
                                    {isEvenSection ? (
                                        <>
                                            <div className={styles.textContent}>
                                                <h3 className={styles.sectionTitle}>{section?.title}</h3>
                                                <ul className={styles.bulletList}>
                                                    {section?.bullets?.map((bullet, bulletIndex) => (
                                                        <li key={bulletIndex}>{bullet}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <img
                                                src={section?.image?.component}
                                                alt={section?.image?.alt}
                                                loading="lazy"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                src={section?.image?.component}
                                                alt={section?.image?.alt}
                                                loading="lazy"
                                            />
                                            <div className={styles.textContent}>
                                                <h3 className={styles.sectionTitle}>{section?.title}</h3>
                                                <ul className={styles.bulletList}>
                                                    {section?.bullets?.map((bullet, bulletIndex) => (
                                                        <li key={bulletIndex}>{bullet}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Footer Button */}
                <div className={styles.footerSection}>
                    <button className={styles.footerButton} onClick={() => { navigate("/") }}>
                        <span>Know More About SchoolAI</span>
                        <svg className={styles.arrowIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>

            {showFloatingButton && (
                <button className={styles.demoButtonFloating} onClick={() => { setOpenDemo(true) }}>
                    {outcome?.buttonText}
                </button>
            )}

            {openDemo &&
                <FormModal isOpen={openDemo} onRequestClose={() => { setOpenDemo(false) }}>
                    <CalendarModal onClose={() => { setOpenDemo(false) }} />
                </FormModal>
            }

        </div>
    )
}
